import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { API_BASE_URL } from "../../../js/api.js";
import { toDateInputValue } from "../../../js/dateHelpers.js";
import {
  FaHandPointer,
  FaHandshake,
  FaEye,
  FaClipboardCheck,
  FaArrowLeft,
} from "react-icons/fa";
import ToastContainer from "../../components/Toast/ToastContainer.js";
import useToast from "../../hooks/useToast.js";
import useConfirm from "../../hooks/useConfirm.js";
import "./style.css";

const formatarDataBR = (dataStr) => {
  if (!dataStr) return "-";
  const d = new Date(dataStr);
  if (isNaN(d.getTime())) return dataStr;
  return d.toLocaleDateString("pt-BR");
};

const formatarDataHoraBR = (dataStr) => {
  if (!dataStr) return "-";
  const d = new Date(dataStr);
  if (isNaN(d.getTime())) return dataStr;
  return d.toLocaleString("pt-BR");
};

const formatarCnpjCpf = (valor) => {
  if (!valor) return "-";
  const nums = String(valor).replace(/\D/g, "").slice(0, 14);
  if (nums.length === 0) return "-";
  if (nums.length <= 11) {
    return nums
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  }
  return nums
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
};

const verificarPermissao = () => {
  const token = localStorage.getItem("token");
  if (!token) return false;
  try {
    const decoded = jwtDecode(token);
    return decoded.tipo === "usuario_ciee";
  } catch {
    return false;
  }
};

const Validacoes = () => {
  const navigate = useNavigate();
  const {
    toasts,
    error: toastError,
    success: toastSuccess,
    removeToast,
  } = useToast();
  const { confirm, confirmDialog } = useConfirm();
  const [abaAtiva, setAbaAtiva] = useState("novos");
  const [novos, setNovos] = useState([]);
  const [minhas, setMinhas] = useState([]);
  const [loadingNovos, setLoadingNovos] = useState(false);
  const [loadingMinhas, setLoadingMinhas] = useState(false);

  const token = localStorage.getItem("token");
  const authHeader = { Authorization: `Bearer ${token}` };

  const tipoDoc = (item) => {
    if ("nome_completo" in item && !("cpf" in item)) return "TCA Empregador";
    if ("cpf" in item) return "TCE";
    return "TCA";
  };

  const rotaDoc = (item) => {
    if ("nome_completo" in item && !("cpf" in item))
      return `/tca-empregador?id=${item.id}`;
    if ("cpf" in item) return `/tce?id=${item.id}`;
    return `/tca?id=${item.id}`;
  };

  const safeGetNovos = async (path, label) => {
    try {
      const r = await axios.get(`${API_BASE_URL}${path}`, {
        headers: authHeader,
      });
      return { ok: true, label, data: r.data || [] };
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      console.error(
        `[carregarNovos] ${label} (${path}) falhou: status=${status}`,
        data || err?.message,
      );
      return { ok: false, label, status, data };
    }
  };

  const carregarNovos = async () => {
    setLoadingNovos(true);
    try {
      const [tca, tce, tcaEmp] = await Promise.all([
        safeGetNovos("/tca/validacoes/novos", "TCA"),
        safeGetNovos("/tce/validacoes/novos", "TCE"),
        safeGetNovos("/tca-empregador/validacoes/novos", "TCA-Empregador"),
      ]);

      // Loga quais falharam para diagnóstico sem quebrar a página.
      const falhas = [tca, tce, tcaEmp].filter((r) => !r.ok);
      if (falhas.length) {
        console.error(
          "[carregarNovos] Endpoints com falha:",
          falhas.map((f) => `${f.label}=${f.status}`).join(", "),
        );
      }

      const ok = [tca, tce, tcaEmp].filter((r) => r.ok).map((r) => r.data);
      setNovos(ok.flat());
    } catch (err) {
      console.error("Erro ao carregar novos:", err);
    } finally {
      setLoadingNovos(false);
    }
  };

  const safeGetMinhas = async (path, label) => {
    try {
      const r = await axios.get(`${API_BASE_URL}${path}`, {
        headers: authHeader,
      });
      return { ok: true, label, data: r.data || [] };
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      console.error(
        `[carregarMinhas] ${label} (${path}) falhou: status=${status}`,
        data || err?.message,
      );
      return { ok: false, label, status, data };
    }
  };

  const carregarMinhas = async () => {
    setLoadingMinhas(true);
    try {
      const [tca, tce, tcaEmp] = await Promise.all([
        safeGetMinhas("/tca/validacoes/minhas", "TCA"),
        safeGetMinhas("/tce/validacoes/minhas", "TCE"),
        safeGetMinhas("/tca-empregador/validacoes/minhas", "TCA-Empregador"),
      ]);

      const falhas = [tca, tce, tcaEmp].filter((r) => !r.ok);
      if (falhas.length) {
        console.error(
          "[carregarMinhas] Endpoints com falha:",
          falhas.map((f) => `${f.label}=${f.status}`).join(", "),
        );
      }

      const ok = [tca, tce, tcaEmp].filter((r) => r.ok).map((r) => r.data);
      setMinhas(ok.flat());
    } catch (err) {
      console.error("Erro ao carregar minhas:", err);
    } finally {
      setLoadingMinhas(false);
    }
  };

  useEffect(() => {
    if (!verificarPermissao()) {
      navigate("/dashboard", { replace: true });
      return;
    }
    carregarNovos();
    carregarMinhas();
    const interval = setInterval(() => {
      carregarNovos();
      carregarMinhas();
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const endpointAtribuir = (item) => {
    if ("nome_completo" in item && !("cpf" in item))
      return `/tca-empregador/validacoes/atribuir/${item.id}`;
    if ("cpf" in item) return `/tce/validacoes/atribuir/${item.id}`;
    return `/tca/validacoes/atribuir/${item.id}`;
  };

  const endpointLiberar = (item) => {
    if ("nome_completo" in item && !("cpf" in item))
      return `/tca-empregador/validacoes/liberar/${item.id}`;
    if ("cpf" in item) return `/tce/validacoes/liberar/${item.id}`;
    return `/tca/validacoes/liberar/${item.id}`;
  };

  const atribuir = async (item) => {
    try {
      await axios.post(
        `${API_BASE_URL}${endpointAtribuir(item)}`,
        {},
        { headers: authHeader },
      );
      toastSuccess("Documento atribuído para você!");
      carregarNovos();
      carregarMinhas();
    } catch (err) {
      toastError(err.response?.data?.erro || "Erro ao atribuir documento.");
    }
  };

  const liberar = async (item) => {
    const ok = await confirm({
      title: "Liberar documento",
      message:
        "Liberar este documento de volta para a fila?\nOutro operador poderá atribuí-lo.",
      confirmText: "Sim, liberar",
      cancelText: "Cancelar",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await axios.post(
        `${API_BASE_URL}${endpointLiberar(item)}`,
        {},
        { headers: authHeader },
      );
      toastSuccess("Documento liberado de volta para a fila.");
      carregarNovos();
      carregarMinhas();
    } catch (err) {
      toastError(err.response?.data?.erro || "Erro ao liberar documento.");
    }
  };

  const numeroDocumento = (item) => {
    const est = item.estudante_id || "N/A";
    const emp = item.empresa_id || "N/A";
    return `${est}-${emp}`;
  };

  const renderTabela = (lista, loading, acao) => {
    if (loading) return <p className="val-loading">Carregando...</p>;
    if (lista.length === 0) {
      return (
        <p className="val-empty">
          {acao === "novos"
            ? "Nenhum documento aguardando atribuição."
            : "Você não tem documentos atribuídos."}
        </p>
      );
    }
    return (
      <table className="val-table">
        <thead>
          <tr>
            <th>Nº Documento</th>
            <th>CNPJ/ID</th>
            <th>Empresa</th>
            <th>Estudante</th>
            <th>Tipo</th>
            <th>Início Vigência</th>
            <th>{acao === "novos" ? "Entrada na fila" : "Atribuído em"}</th>
            <th>Status</th>
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          {lista.map((item) => {
            const key = `${tipoDoc(item)}-${item.id}`;
            return (
              <tr key={key}>
                <td>{numeroDocumento(item)}</td>
                <td>{formatarCnpjCpf(item.empresa?.cnpj)}</td>
                <td>{item.empresa?.razaosocial || "-"}</td>
                <td>
                  {item.nome_aprendiz ||
                    item.nome_completo ||
                    item.nome_estagiario ||
                    "-"}
                </td>
                <td>
                  <span className="val-tipo-badge">{tipoDoc(item)}</span>
                </td>
                <td>
                  {item.data_inicio_contrato
                    ? formatarDataBR(
                        toDateInputValue(item.data_inicio_contrato) +
                          "T00:00:00",
                      )
                    : "-"}
                </td>
                <td>
                  {acao === "novos"
                    ? formatarDataHoraBR(item.created_at)
                    : formatarDataHoraBR(item.atribuido_em)}
                </td>
                <td>
                  <span
                    className={`val-status-badge val-status-${(
                      item.status || "PENDENTE"
                    ).toLowerCase()}`}
                  >
                    {item.status || "PENDENTE"}
                  </span>
                </td>
                <td className="val-acoes">
                  {acao === "novos" ? (
                    <button
                      className="val-btn-icon val-btn-attr"
                      onClick={() => atribuir(item)}
                      title="Atribuir para mim"
                    >
                      <FaHandPointer />
                    </button>
                  ) : (
                    <>
                      <button
                        className="val-btn-icon val-btn-view"
                        onClick={() => navigate(rotaDoc(item))}
                        title="Abrir documento"
                      >
                        <FaEye />
                      </button>
                      <button
                        className="val-btn-icon val-btn-lib"
                        onClick={() => liberar(item)}
                        title="Liberar"
                      >
                        <FaHandshake />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  if (!verificarPermissao()) return null;

  return (
    <div className="val-container">
      <header className="val-header">
        <div className="val-header-titulo">
          <FaClipboardCheck size={26} />
          <h1>Validações — Fila de Documentos</h1>
        </div>
        <button
          className="val-btn val-btn-secondary"
          onClick={() => navigate("/dashboard")}
        >
          <FaArrowLeft /> Voltar
        </button>
      </header>

      <div className="val-tabs">
        <button
          className={`val-tab ${abaAtiva === "novos" ? "val-tab-ativa" : ""}`}
          onClick={() => setAbaAtiva("novos")}
        >
          Novos
          {novos.length > 0 && (
            <span className="val-tab-badge">{novos.length}</span>
          )}
        </button>
        <button
          className={`val-tab ${abaAtiva === "minhas" ? "val-tab-ativa" : ""}`}
          onClick={() => setAbaAtiva("minhas")}
        >
          Minhas Validações
          {minhas.length > 0 && (
            <span className="val-tab-badge val-tab-badge-minhas">
              {minhas.length}
            </span>
          )}
        </button>
      </div>

      {abaAtiva === "novos" && (
        <section className="val-section">
          <h2>Documentos Aguardando Atribuição</h2>
          {renderTabela(novos, loadingNovos, "novos")}
        </section>
      )}

      {abaAtiva === "minhas" && (
        <section className="val-section">
          <h2>Meus Documentos em Validação</h2>
          {renderTabela(minhas, loadingMinhas, "minhas")}
        </section>
      )}

      <ToastContainer toasts={toasts} onRemove={removeToast} />
      {confirmDialog}
    </div>
  );
};

export default Validacoes;
